import { useQuery } from '@apollo/client';
import * as stripe from '@stripe/stripe-js';
import axios from 'axios';
import { graphql, useStaticQuery } from 'gatsby';
import latestFlowResponsesForSessionQuery from 'graphql/queries/latestFlowResponsesForSession.gql';
import React, { FC, useContext, useState } from 'react';
import { useAsync } from 'react-use';
import { v4 as uuidv4 } from 'uuid';

import { dataConstants as insuranceStatusConstants } from '@ui/routes/Membership/MembershipInsuranceStatusStep/MembershipInsuranceStatusStep.data';

import useFlowData from '@hooks/useFlowData';

import { keysToCamel } from '@utils/butter';
import { ApiEndpoints } from '@utils/endpoints';

import { useAuth } from '@providers/AuthProvider';
import FlowContext from '@providers/FlowContextProvider';
import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { ExemptionType } from '@data/insurance';

import { IFlowStepProps } from '@folx-types/flow-steps';
import { IPricingRequest, IPricingResponse } from '@folx-types/pricing';

import companyInfoQuery from '@graphql/queries/companyInfo.query.gql';
import isMembershipWaivableQuery from '@graphql/queries/isMembershipWaivable.query.gql';

import MembershipSelectionStep, {
	ISelectionStepPageContent,
} from './MembershipSelection.component';
import { dataConstants } from './MembershipSelection.data';

interface IMembershipSelectionStepPageProps extends IFlowStepProps {}

const MembershipSelectionStepPage: FC<IMembershipSelectionStepPageProps> = ({ path, stepKey }) => {
	const [priceLoading, setLoading] = useState(true);
	const [loadingStripe, setLoadingStripe] = useState(false);
	const [pricingResponse, setPricingResponse] = useState<IPricingResponse | undefined>(undefined);
	const [annualPricingResponse, setAnnualPricingResponse] = useState<IPricingResponse | undefined>(
		undefined
	);

	const { existingFormData, flowId } = useContext(FlowContext);
	const { submitFormData } = useFlowData(flowId);
	const { email, getAuthToken } = useAuth();

	const { pageContent } = useStaticQuery(
		graphql`
			{
				pageContent: butterPage(slug: { eq: "membership-plan-selection" }) {
					folx4folx_header
					folx4folx_info
					page_header
					company_header
					company_info
					insurance_header
					insurance_info
					disclaimer
					membership_header
					membership_steps {
						info
					}
					folx_membership_option_monthly {
						title
						price
						description
						price_subtext
						cta
					}
					folx_membership_option_yearly {
						title
						price
						badge
						description
						cta
					}
				}
			}
		`
	);

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);

	const { loading: companyLoading, data: companyData } = useQuery(companyInfoQuery, {
		client: graphQLv2Client,
	});

	// We are hard-fetching flowResponses here to get the formUuid, as this flowResponse
	// is generated in the backend and only propogates to flowReponses on the frontend if
	// the page is reloaded (in the case of appointment booking via upvio for example).
	const { data: prevFlowResponses, loading: prevFlowResponsesLoading } = useQuery(
		latestFlowResponsesForSessionQuery,
		{ skip: existingFormData.formUuid }
	);

	const isWaivable = !!existingFormData?.[insuranceStatusConstants.isWaivable];
	const exemptBy = existingFormData?.[insuranceStatusConstants.exemptBy] as
		| ExemptionType
		| undefined;

	const { loading: isMembershipWaivableLoading, data: isMembershipWaivableData } = useQuery(
		isMembershipWaivableQuery,
		{
			client: graphQLv2Client,
			variables: {
				stateOfResidence: existingFormData?.shippingState,
				dob: existingFormData?.dob,
			},
			skip:
				insuranceStatusConstants.isWaivable in existingFormData &&
				insuranceStatusConstants.exemptBy in existingFormData,
		}
	);

	useAsync(async () => {
		const pricingRequest = {
			customerEmail: email,
			billingFrequency: dataConstants.MONTHLY_MEMBERSHIP,
			stateOfResidence: existingFormData?.shippingState,
		};

		const annualPricingRequest = {
			customerEmail: email,
			billingFrequency: dataConstants.ANNUAL_MEMBERSHIP,
			stateOfResidence: existingFormData?.shippingState,
		};

		const authorizationToken = await getAuthToken();

		const requestConfig = {
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
				authorizationToken,
			},
		};

		axios
			.all([
				axios.post<IPricingResponse>(ApiEndpoints.pricing, pricingRequest, requestConfig),
				axios.post<IPricingResponse>(ApiEndpoints.pricing, annualPricingRequest, requestConfig),
			])
			.then((result: { data: IPricingResponse }[]) => {
				console.log(result);
				setPricingResponse(result[0].data);
				setAnnualPricingResponse(result[1].data);
				setLoading(false);
			})
			.catch((e) => {
				console.log(`caught error: ${JSON.stringify(e)}`);
			});
	}, []);

	const submit = async (data: Record<string, string>) => {
		try {
			setLoadingStripe(true);
			const pubKey = email.toLowerCase().endsWith('@folxhealth.com')
				? process.env.GATSBY_STRIPE_TEST_PUBLISHABLE_KEY
				: process.env.GATSBY_STRIPE_PUBLISHABLE_KEY;
			const stripeClient = await stripe.loadStripe(pubKey!);

			const authorizationToken = await getAuthToken();

			if (!existingFormData.stripeIdempotencyKey) {
				existingFormData.stripeIdempotencyKey = uuidv4();

				await submitFormData({
					stripeIdempotencyKey: existingFormData.stripeIdempotencyKey,
				});
			}

			const formUuid =
				existingFormData.formUuid ||
				prevFlowResponses?.latestFlowResponsesForCurrentMemberSession?.nodes?.find(
					(response: Record<'key' | 'value', never>) => response.key === 'formUuid'
				)?.value;

			const pricingRequest: IPricingRequest & {
				redirect_host?: string;
				idempotency_key?: string;
			} = {
				billingFrequency: data.membershipDuration,
				flow_session_uuid: formUuid,
				...(existingFormData.stripeIdempotencyKey && {
					idempotency_key: existingFormData.stripeIdempotencyKey,
				}),
			};
			if (window?.location) {
				pricingRequest.redirect_host = `${window.location.protocol}//${window.location.host}`;
			}

			const response = await axios.post(`${ApiEndpoints.stripe}/session`, pricingRequest, {
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
					authorizationToken,
				},
			});

			const stripeSessionId = response.data.checkoutSessionId;

			// Must change the idempotency key if the anchor is different
			if (
				existingFormData.billingAnchor &&
				existingFormData.billingAnchor !== response.data.billingAnchor
			) {
				existingFormData.stripeIdempotencyKey = uuidv4();
				await submitFormData({
					stripeIdempotencyKey: existingFormData.stripeIdempotencyKey,
					membershipDuration: data.membershipDuration,
				});
			}
			existingFormData.billingAnchor = response.data.billingAnchor;
			await submitFormData({
				stripeSessionId,
				billingAnchor: existingFormData.billingAnchor,
				membershipDuration: data.membershipDuration,
			});
			await stripeClient?.redirectToCheckout({
				sessionId: stripeSessionId,
			});
		} catch (e) {
			existingFormData.stripeIdempotencyKey = uuidv4();
			await submitFormData({
				stripeIdempotencyKey: existingFormData.stripeIdempotencyKey,
				membershipDuration: data.membershipDuration,
			});
			setLoadingStripe(false);
			console.log(`error: ${JSON.stringify(e)}`, e);
		}
	};

	const refinedContent: ISelectionStepPageContent = keysToCamel(
		pageContent
	) as ISelectionStepPageContent;

	const loading =
		priceLoading || companyLoading || isMembershipWaivableLoading || prevFlowResponsesLoading;
	const companyName = companyData?.authenticatedMember?.b2bCustomer?.name;

	return (
		<MembershipSelectionStep
			path={path}
			stepKey={stepKey}
			loading={loading}
			loadingStripe={loadingStripe}
			existingFormData={existingFormData}
			submitFunc={submit}
			pricingResponse={pricingResponse}
			annualPricingResponse={annualPricingResponse}
			company={companyName}
			pageContent={refinedContent}
			isMembershipWaivable={
				insuranceStatusConstants.isWaivable in existingFormData &&
				insuranceStatusConstants.exemptBy in existingFormData
					? { isWaivable, exemptBy }
					: isMembershipWaivableData?.authenticatedMember?.isMembershipWaivable
			}
		/>
	);
};

export default MembershipSelectionStepPage;
